import React, { useEffect, useMemo, useState } from 'react';
import { LayoutGrid, Printer, Loader2, AlertCircle } from 'lucide-react';
import apiClient from '../../config/api.config';

interface Item { matiere: string; classe: string; formateur: string; }
interface SalleOcc {
  id: number; nom: string; capacite: number; equipements: string[];
  etat_manuel: string; etat_motif: string; batiment: string;
  grille: Record<string, Record<string, Item[]>>;
}
interface Occ {
  annee: string; slots: { debut: string; fin: string }[]; jours: string[];
  salles: SalleOcc[]; salles_non_appariees: Record<string, number>;
}
const JOUR_LABEL: Record<string, string> = {
  lundi: 'Lundi', mardi: 'Mardi', mercredi: 'Mercredi', jeudi: 'Jeudi', vendredi: 'Vendredi', samedi: 'Samedi',
};
const fmtH = (h: string) => h.slice(0, 5).replace(':', 'h');
const jourAuj = (): string => (['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][new Date().getDay()]);

const OccupationSalles: React.FC = () => {
  const [annees, setAnnees] = useState<{ libelle: string; active: boolean }[]>([]);
  const [annee, setAnnee] = useState('');
  const [semestre, setSemestre] = useState('1');
  const [jour, setJour] = useState(() => { const j = jourAuj(); return j === 'dimanche' ? 'lundi' : j; });
  const [occ, setOcc] = useState<Occ | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    apiClient.get('/migration-reference/annees/').then((r) => {
      const l = r.data.annees || []; setAnnees(l);
      const a = l.find((x: any) => x.active); if (a) setAnnee(a.libelle);
    }).catch(() => {});
  }, []);

  const charger = () => {
    if (!annee) return;
    setLoading(true); setMsg(null);
    apiClient.get('/vie-scolaire/occupation/', { params: { semestre }, headers: { 'X-Annee-Academique': annee } })
      .then((r) => setOcc(r.data))
      .catch(() => setMsg('Chargement de l’occupation impossible.'))
      .finally(() => setLoading(false));
  };
  useEffect(charger, [annee, semestre]);

  const cellColor = (s: SalleOcc, items: Item[] | undefined): string => {
    if (s.etat_manuel === 'maintenance') return 'bg-red-100 dark:bg-red-900/30';
    if (items && items.length) return 'bg-green-100 dark:bg-green-900/30';
    return '';
  };

  const imprimerFiche = async () => {
    if (!occ) return;
    const inst = await apiClient.get('/parametres/info-institut/').then((r) => r.data).catch(() => ({}));
    const esc = (v: any) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const slots = occ.slots;
    const ville = inst.ville || 'Pointe-Noire';
    const agr = inst.numero_agrement || "Agrément N° 0196/MES – CAB.DG-DAAC du 21/04/2021";
    const tel = inst.telephone || ''; const email = inst.email || ''; const site = inst.site_web || 'www.institut-international-2i.com';

    const corps = occ.salles.map((s) => {
      return occ.jours.map((j, ji) => {
        const first = ji === 0;
        const rowspan = occ.jours.length;
        let tds = '';
        if (first) {
          tds += `<td class="sn" rowspan="${rowspan}"><b>${esc(s.nom)}</b></td>`
            + `<td class="cap" rowspan="${rowspan}">${s.capacite || ''}</td>`
            + `<td class="eq" rowspan="${rowspan}">${(s.equipements || []).map(esc).join(', ')}</td>`;
        }
        tds += `<td class="jr">${JOUR_LABEL[j]}</td>`;
        slots.forEach((_sl, si) => {
          if (s.etat_manuel === 'maintenance') { tds += `<td class="st red">Indisponible</td>`; return; }
          const items = (s.grille[j] || {})[si] || [];
          if (!items.length) { tds += `<td></td>`; return; }
          const inner = items.map((it) => `<div class="c"><div class="no">${esc(it.classe)}</div>`
            + `<div class="ma">${esc(it.matiere)}</div>`
            + `<div class="pr">${esc(it.formateur || 'xxxxx')}</div></div>`).join('');
          tds += `<td class="st green">${inner}</td>`;
        });
        return `<tr>${tds}</tr>`;
      }).join('');
    }).join('');

    const headSlots = slots.map((sl) => `<th>${fmtH(sl.debut)}<br>–<br>${fmtH(sl.fin)}</th>`).join('');
    const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Fiche occupation des salles</title>
      <style>
        @page { size: A4 landscape; margin: 8mm; }
        * { box-sizing: border-box; }
        body { font-family: "Trebuchet MS", "Segoe UI", Arial, sans-serif; color: #1a1a1a; margin: 0; font-size: 8.5px; }
        .head { display: flex; align-items: center; gap: 12px; margin-bottom: 6px; }
        .head img { width: 54px; height: 54px; object-fit: contain; }
        .head .c { flex: 1; text-align: center; }
        .head .c .t { font-weight: bold; font-size: 1.5em; color: #0b3d91; letter-spacing: .5px; }
        .head .c .s { font-size: .95em; }
        .head .an { text-align: right; font-size: .95em; }
        .meta { display: flex; gap: 26px; margin: 4px 2px 6px; font-size: .95em; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #555; padding: 2px 3px; text-align: center; vertical-align: middle; }
        thead th { background: #f3f3f3; font-size: .9em; }
        td.sn { font-weight: bold; width: 70px; }
        td.cap { width: 42px; } td.eq { width: 120px; font-size: .9em; text-align: left; }
        td.jr { width: 56px; font-weight: bold; background: #fafafa; }
        td.st { padding: 1px; }
        td.green { background: #d9ead3; } td.red { background: #f4cccc; font-style: italic; }
        .c .no { font-weight: bold; } .c .ma { } .c .pr { font-style: italic; color: #333; font-size: .92em; }
        .legende { display: flex; gap: 20px; margin-top: 8px; font-size: .95em; }
        .lg { display: inline-flex; align-items: center; gap: 5px; }
        .sw { width: 18px; height: 11px; border: 1px solid #999; display: inline-block; }
        .visas { display: flex; justify-content: flex-end; gap: 40px; margin-top: 10px; font-size: .95em; }
        .foot { margin-top: 10px; border-top: 2px solid #9db8d2; padding-top: 4px; text-align: center; font-size: .85em; color: #777; }
        .foot .agr { color: #b06; }
      </style></head><body>
      <div class="head">
        <img src="/logo.png" alt="logo">
        <div class="c"><div class="t">FICHE DE PLANNING HEBDOMADAIRE D'OCCUPATION DES SALLES</div>
          <div class="s">INSTITUT INTERNATIONAL 2i &bull; Grille d'occupation par Salle</div></div>
        <div class="an">Année académique<br><b>${esc(occ.annee)}</b></div>
      </div>
      <div class="meta"><div>SEMAINE DU __________ AU __________</div><div>SITE : ${esc(ville)}</div></div>
      <table>
        <thead><tr>
          <th>Numéro / Nom de salle</th><th>Capacité</th><th>Équipements fixes</th><th>Jours</th>${headSlots}
        </tr></thead>
        <tbody>${corps}</tbody>
      </table>
      <div class="legende">
        <span class="lg"><span class="sw" style="background:#d9ead3"></span>Salle occupée (Cours régulier)</span>
        <span class="lg"><span class="sw" style="background:#cfe2f3"></span>Événement / Réunion / Examen</span>
        <span class="lg"><span class="sw" style="background:#fff"></span>Salle libre / Disponible</span>
        <span class="lg"><span class="sw" style="background:#f4cccc"></span>Salle indisponible (Travaux / Maintenance)</span>
      </div>
      <div class="visas">
        <div>Fiche établie par : ____________________</div>
        <div>Date de mise à jour : ____ / ____ / ______</div>
        <div>Signature du responsable : ____________________</div>
      </div>
      <div class="foot">
        <div class="agr">${esc(agr)}</div>
        <div>CAMPUS DE ${esc((ville || '').toUpperCase())}${tel ? ' – Tél. : ' + esc(tel) : ''}${email ? ' – ' + esc(email) : ''} – Site : ${esc(site)}</div>
      </div>
      <script>window.addEventListener('load',function(){setTimeout(function(){try{window.focus();window.print();}catch(e){}},300);});</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { setMsg('Autorisez les popups pour imprimer.'); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  const nbNonAppariees = useMemo(() => Object.keys(occ?.salles_non_appariees || {}).length, [occ]);

  return (
    <div className="p-6 max-w-full mx-auto">
      <div className="mb-5 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <LayoutGrid size={26} /> Occupation des salles
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Vue hebdomadaire de l'occupation, à partir des emplois du temps.</p>
        </div>
        <button onClick={imprimerFiche} disabled={!occ}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
          <Printer size={16} /> Fiche hebdomadaire
        </button>
      </div>

      {msg && <div className="mb-4 p-3 rounded-lg flex items-center gap-2 text-sm bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"><AlertCircle size={18} /><span>{msg}</span></div>}

      <div className="flex items-end gap-3 mb-4 flex-wrap">
        <div><label className="block text-xs text-gray-500 mb-1">Année</label>
          <select value={annee} onChange={(e) => setAnnee(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            {annees.map((a) => <option key={a.libelle} value={a.libelle}>{a.libelle}{a.active ? ' (active)' : ''}</option>)}
          </select></div>
        <div><label className="block text-xs text-gray-500 mb-1">Semestre</label>
          <select value={semestre} onChange={(e) => setSemestre(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            <option value="1">S1</option><option value="2">S2</option>
          </select></div>
        <div><label className="block text-xs text-gray-500 mb-1">Jour (vue)</label>
          <select value={jour} onChange={(e) => setJour(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            {(occ?.jours || Object.keys(JOUR_LABEL)).map((j) => <option key={j} value={j}>{JOUR_LABEL[j]}</option>)}
          </select></div>
        {loading && <Loader2 size={18} className="animate-spin text-gray-400 mb-2" />}
        <div className="ml-auto flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-green-200"></span>Occupée</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-200"></span>Indisponible</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm border border-gray-300"></span>Libre</span>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300">
            <tr>
              <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 dark:bg-gray-700/50">Salle</th>
              {(occ?.slots || []).map((sl, i) => <th key={i} className="px-3 py-2 whitespace-nowrap">{fmtH(sl.debut)}–{fmtH(sl.fin)}</th>)}
            </tr>
          </thead>
          <tbody>
            {(occ?.salles || []).map((s) => (
              <tr key={s.id} className="border-t border-gray-100 dark:border-gray-700">
                <td className="px-3 py-2 sticky left-0 bg-white dark:bg-gray-800">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{s.nom}</div>
                  <div className="text-xs text-gray-400">{s.capacite ? `${s.capacite} pl.` : ''}{s.batiment ? ` · ${s.batiment}` : ''}</div>
                </td>
                {(occ?.slots || []).map((_sl, si) => {
                  const items = (s.grille[jour] || {})[si];
                  return (
                    <td key={si} className={`px-2 py-1 align-top border-l border-gray-100 dark:border-gray-700 ${cellColor(s, items)}`}>
                      {s.etat_manuel === 'maintenance' ? (
                        <span className="text-xs text-red-700 dark:text-red-300 italic">Indisponible{s.etat_motif ? ` (${s.etat_motif})` : ''}</span>
                      ) : items && items.length ? (
                        items.map((it, k) => (
                          <div key={k} className="mb-1 last:mb-0">
                            <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">{it.classe}</div>
                            <div className="text-xs text-gray-700 dark:text-gray-300">{it.matiere}</div>
                            <div className="text-[11px] italic text-gray-500">{it.formateur}</div>
                          </div>
                        ))
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
            {(!occ || occ.salles.length === 0) && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">Aucune salle. Ajoutez/importez des salles dans « Salles ».</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {nbNonAppariees > 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
          {nbNonAppariees} libellé(s) de salle de l'EDT sans correspondance dans le registre (ex. « {Object.keys(occ!.salles_non_appariees)[0]} ») — ajoute ces salles ou aligne leur nom.
        </p>
      )}
    </div>
  );
};

export default OccupationSalles;
